<template>
  <div>
    <transition name="fade">
      <div v-if="savingOrder" class="kb-order-saving">
        <i class="fal fa-spinner fa-spin mr-2"></i>Salvando ordem...
      </div>
    </transition>

    <v-layout>
      <div class="boxapp">
        <!-- boxapp head -->
        <div class="boxapp-head">
          <a href="#" @click="$router.go(-1)">
            <faIcon type="fal" name="long-arrow-left" size="24" colorFont="#cfd2dd" />
          </a>
          <h1>{{ project.type }}</h1>
        </div>

        <!-- boxapp body -->
        <div class="box-main">
          <div class="section">
            <v-layout>
              <div class="head w-100 mt-0">
                <h4>Perguntas e respostas</h4>
              </div>
              <div class="d-flex">
                <v-btn
                  flat
                  round
                  class="btn px-4 mr-2"
                  large
                  @click="openImport"
                >
                  <i class="mr-2 fal fa-file-upload"></i>Importar JSON
                </v-btn>
                <v-btn
                  flat
                  round
                  color="white"
                  class="btn btn-primary px-4"
                  large
                  @click="openCreateQuestion()"
                >
                  <i class="mr-2 fas fa-plus"></i>Nova Pergunta
                </v-btn>
              </div>
            </v-layout>

            <p class="kb-help text-gray-dark">
              Mostramos as perguntas da <strong>Base de Conhecimento</strong> compatíveis com a lei
              de incentivo desta campanha (marcadas como <span class="kb-badge padrao">Padrão</span>)
              junto das perguntas <span class="kb-badge personalizada">Personalizadas</span> criadas
              só para esta campanha. As perguntas Padrão não são editáveis aqui — para cada uma você
              escolhe: <strong>Visível ao doador</strong>, <strong>Somente IA</strong> (oculta do doador,
              mas o agente acessa) ou <strong>Desativada</strong> (não aparece pro doador nem para a IA
              nesta campanha — use ao substituir uma pergunta padrão por uma personalizada).
            </p>

            <div class="wrapper pt-3 mw-total kb-tree">
              <p v-if="displayTree.length === 0" class="text-gray-dark">
                Nenhuma categoria com perguntas para esta campanha.
              </p>

              <div
                v-for="cat in displayTree"
                :key="cat.id"
                class="kb-cat"
                :class="{ 'kb-cat--open': isCatOpen(cat.id) }"
              >
                <div class="kb-cat__head">
                  <button type="button" class="kb-cat__toggle" @click="toggleCat(cat.id)">
                    <i class="fal fa-chevron-down kb-cat__chevron"></i>
                    <span class="kb-cat__name">{{ cat.name }}</span>
                    <span class="kb-cat__badge">{{ countCategoryQuestions(cat) }}</span>
                  </button>
                </div>

                <div v-show="isCatOpen(cat.id)" class="kb-cat__body">
                  <div v-for="sub in cat.subcategories" :key="sub.id" class="kb-sub">
                    <div class="kb-sub__head">
                      <span class="kb-sub__name">{{ sub.name }}</span>
                      <v-btn
                        v-if="sub.id > 0"
                        icon
                        small
                        @click="openCreateQuestion(sub)"
                        title="Nova pergunta nesta subcategoria"
                      >
                        <v-icon size="14">fal fa-plus</v-icon>
                      </v-btn>
                    </div>

                    <ul class="kb-questions">
                      <li
                        v-for="faq in sub.faqs"
                        :key="faq.source + '_' + faq.id"
                        class="kb-question"
                        :class="{ 'kb-question--disabled': !faq.isActive }"
                      >
                        <div class="kb-question__text">
                          <h5 :title="faq.question">
                            <span class="kb-badge" :class="badgeClass(faq)">{{ badgeLabel(faq) }}</span>
                            {{ faq.question }}
                          </h5>
                          <p class="text-gray-dark" :title="stripHtml(faq.answer)">{{ stripHtml(faq.answer) }}</p>
                        </div>
                        <div class="kb-question__controls">
                          <!-- Pergunta padrao (KB): 3 estados (visivel / somente IA / desativada nesta campanha) -->
                          <div v-if="faq.source === 'KnowledgeBase'" class="kb-state-select">
                            <InputSelect
                              :items="visibilityStates"
                              valueAttribute="value"
                              textAttribute="label"
                              :hasUnselectedOption="false"
                              :value="kbState(faq)"
                              @input="setKbState(faq, $event)"
                            />
                          </div>
                          <!-- Pergunta personalizada: visivel ao doador x somente IA + ordenar + editar/excluir -->
                          <template v-else>
                            <v-switch
                              :input-value="faq.isVisibleToDonor"
                              @change="toggleVisibility(faq, $event)"
                              :label="faq.isVisibleToDonor ? 'Visível ao doador' : 'Somente IA'"
                              hide-details
                              class="kb-switch"
                            />
                            <v-btn
                              icon
                              small
                              :disabled="campaignPos(sub, faq) === 0"
                              @click="moveCampaignQuestion(sub, faq, -1)"
                              title="Mover para cima"
                            >
                              <v-icon size="14">fal fa-arrow-up</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              small
                              :disabled="campaignPos(sub, faq) === campaignCount(sub) - 1"
                              @click="moveCampaignQuestion(sub, faq, 1)"
                              title="Mover para baixo"
                            >
                              <v-icon size="14">fal fa-arrow-down</v-icon>
                            </v-btn>
                            <v-btn icon small @click="openEditQuestion(faq)" title="Editar">
                              <v-icon size="14">fal fa-pencil</v-icon>
                            </v-btn>
                            <v-btn icon small @click="confirmDelete(faq)" title="Excluir">
                              <v-icon size="14">fal fa-trash</v-icon>
                            </v-btn>
                          </template>
                        </div>
                      </li>
                      <li v-if="sub.faqs.length === 0" class="kb-empty-hint text-gray-dark">
                        Nenhuma pergunta nesta subcategoria.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="box-help-spacer"></div>
      <HelpBox :text="helpBoxText" />
    </v-layout>

    <ModalCampaignFaq ref="modalQuestion" :categories="categories" @saved="reload" />
    <ModalImportFaqJson
      ref="modalImport"
      :categories="categories"
      :campaign-id="currentCampaignId"
      @imported="onImported"
    />
    <AucAlert
      ref="removeCampaignFaqAlert"
      title="Você tem certeza que deseja remover essa pergunta da campanha?"
      confirmButtonText="Sim"
      cancelButtonText="Não"
      @confirm="executeDelete"
    />
  </div>
</template>

<script type="plain/text">
import AucAlert from "@/components/AucAlert.vue";
import HelpBox from "@/components/HelpBox.vue";
import { HelpBoxText } from "@/scripts/models/enums/helpBoxText.enum.js";
import CampaignService from "@/scripts/services/campaign.service";
import FaqService from "@/scripts/services/faq.service";
import ModalCampaignFaq from "@/views/app/campaign/modal/ModalCampaignFaq.vue";
import ModalImportFaqJson from "@/views/app/campaign/modal/ModalImportFaqJson.vue";

export default {
  components: {
    AucAlert,
    HelpBox,
    ModalCampaignFaq,
    ModalImportFaqJson
  },
  props: ["pageOptions", "campaignId"],
  data() {
    return {
      campaignService: new CampaignService(),
      faqService: new FaqService(),
      tree: [],
      categories: [],
      openCats: [],
      pendingDelete: null,
      savingOrder: false,
      helpBoxText: HelpBoxText.CampaignFaq,
      visibilityStates: [
        { value: "visible", label: "Visível ao doador" },
        { value: "ai", label: "Somente IA" },
        { value: "disabled", label: "Desativada" }
      ],
      project: {
        type: "Cadastro FAQ"
      }
    };
  },
  created() {
    if (this.pageOptions) this.pageOptions.changeClass(null);
    this.reload();
  },
  computed: {
    currentCampaignId() {
      return Number(this.$route.query.campaignId || this.campaignId);
    },
    // Esconde subcategorias/categorias sem perguntas para esta campanha (deixa a visão enxuta).
    displayTree() {
      return this.tree
        .map(cat => {
          const subcategories = (cat.subcategories || []).filter(
            sub => sub.faqs && sub.faqs.length > 0
          );
          return subcategories.length > 0 ? { ...cat, subcategories } : null;
        })
        .filter(Boolean);
    }
  },
  methods: {
    reload() {
      this.faqService.treeForAdmin(this.currentCampaignId).then(data => {
        this.tree = Array.isArray(data) ? data : [];
        // Abre a primeira categoria (com perguntas) por padrão.
        if (this.openCats.length === 0 && this.displayTree.length > 0) {
          this.openCats = [this.displayTree[0].id];
        }
      });
      this.faqService.listAllCategories().then(data => {
        this.categories = Array.isArray(data) ? data : [];
      });
    },
    countCategoryQuestions(cat) {
      return (cat.subcategories || []).reduce(
        (sum, sub) => sum + (sub.faqs ? sub.faqs.length : 0),
        0
      );
    },
    isCatOpen(catId) {
      return this.openCats.indexOf(catId) !== -1;
    },
    toggleCat(catId) {
      const i = this.openCats.indexOf(catId);
      if (i === -1) this.openCats.push(catId);
      else this.openCats.splice(i, 1);
    },
    badgeLabel(faq) {
      return faq.source === "Campaign" ? "Personalizada" : "Padrão";
    },
    badgeClass(faq) {
      return faq.source === "Campaign" ? "personalizada" : "padrao";
    },
    // Remove tags HTML pro preview/tooltip (a resposta pode ser HTML do editor).
    stripHtml(html) {
      return (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    },
    openCreateQuestion(sub) {
      const options = sub && sub.id > 0 ? { subcategoryId: sub.id } : {};
      this.$refs.modalQuestion.open(this.currentCampaignId, null, options);
    },
    openImport() {
      this.$refs.modalImport.open();
    },
    onImported(count) {
      this.$store.commit(
        "SET_SNACKBAR_MESSAGE",
        `${count} ${count === 1 ? "pergunta importada" : "perguntas importadas"} com sucesso.`
      );
      this.reload();
    },
    openEditQuestion(faq) {
      this.$refs.modalQuestion.open(this.currentCampaignId, faq);
    },
    // Perguntas personalizadas (Campaign) de uma subcategoria, na ordem exibida.
    campaignFaqsOf(sub) {
      return (sub.faqs || []).filter(f => f.source === "Campaign");
    },
    campaignPos(sub, faq) {
      return this.campaignFaqsOf(sub).findIndex(f => f.id === faq.id);
    },
    campaignCount(sub) {
      return this.campaignFaqsOf(sub).length;
    },
    // Personalizadas primeiro (na ordem da campanha), depois as Padrao (KB).
    sortFaqs(a, b) {
      const aGroup = a.source === "Campaign" ? 0 : 1;
      const bGroup = b.source === "Campaign" ? 0 : 1;
      if (aGroup !== bGroup) return aGroup - bGroup;
      const ao = a.sortOrder == null ? Infinity : a.sortOrder;
      const bo = b.sortOrder == null ? Infinity : b.sortOrder;
      if (ao !== bo) return ao - bo;
      return a.id - b.id;
    },
    async moveCampaignQuestion(sub, faq, direction) {
      const campaignFaqs = this.campaignFaqsOf(sub);
      const idx = campaignFaqs.findIndex(f => f.id === faq.id);
      const target = idx + direction;
      if (target < 0 || target >= campaignFaqs.length) return;

      // Otimista: reordena entre as personalizadas e reindexa o sortOrder nos objetos.
      const ordered = campaignFaqs.slice();
      const [moved] = ordered.splice(idx, 1);
      ordered.splice(target, 0, moved);

      const toSave = [];
      ordered.forEach((cf, i) => {
        const newOrder = i + 1;
        if (cf.sortOrder !== newOrder) {
          this.$set(cf, "sortOrder", newOrder);
          toSave.push({
            id: cf.id,
            campaignId: this.currentCampaignId,
            question: cf.question,
            answer: cf.answer,
            subcategoryId: cf.subcategoryId,
            isVisibleToDonor: cf.isVisibleToDonor,
            sortOrder: newOrder
          });
        }
      });
      // Reordena a lista exibida (personalizadas primeiro, na nova ordem).
      sub.faqs.sort(this.sortFaqs);
      if (toSave.length === 0) return;

      this.savingOrder = true;
      try {
        await this.campaignService.saveFaqOrder(toSave);
      } catch (e) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Não foi possível salvar a nova ordem.");
        this.reload();
      } finally {
        this.savingOrder = false;
      }
    },
    // Pergunta personalizada (da campanha): alterna visível ao doador x somente IA.
    toggleVisibility(faq, value) {
      this.campaignService.saveFaq(this.reload, [
        {
          id: faq.id,
          campaignId: this.currentCampaignId,
          question: faq.question,
          answer: faq.answer,
          subcategoryId: faq.subcategoryId,
          isVisibleToDonor: value
        }
      ]);
    },
    // Estado atual de uma pergunta padrão (KB) nesta campanha.
    kbState(faq) {
      if (!faq.isActive) return "disabled";
      return faq.isVisibleToDonor ? "visible" : "ai";
    },
    // Grava o override (visibilidade + ativo) de uma pergunta padrão (KB) nesta campanha.
    setKbState(faq, state) {
      const isActive = state !== "disabled";
      const isVisibleToDonor = state === "visible";
      this.campaignService
        .saveFaqOverride({
          campaignId: this.currentCampaignId,
          knowledgeBaseFaqId: faq.id,
          isVisibleToDonor: isVisibleToDonor,
          isActive: isActive
        })
        .then(this.reload);
    },
    confirmDelete(faq) {
      this.pendingDelete = faq;
      this.$refs.removeCampaignFaqAlert.open();
    },
    executeDelete() {
      if (!this.pendingDelete) return;
      this.campaignService.deleteFaq(() => {
        this.pendingDelete = null;
        this.reload();
      }, this.pendingDelete.id);
    }
  }
};
</script>

<style scoped>
.kb-order-saving {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: #4c4d4f;
  color: #fff;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.kb-help {
  font-size: 13px;
  margin: 8px 0 16px;
  line-height: 1.5;
}
.kb-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  margin-right: 6px;
  vertical-align: middle;
}
.kb-badge.padrao {
  background: #eef0f5;
  color: #555;
}
.kb-badge.personalizada {
  background: rgba(2, 172, 180, 0.12);
  color: #02acb4;
}

/* Categoria */
.kb-cat {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
}
.kb-cat--open {
  border-color: #02acb4;
}
.kb-cat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fafbfc;
}
.kb-cat__toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}
.kb-cat__chevron {
  color: #999;
  transition: transform 0.2s;
}
.kb-cat--open .kb-cat__chevron {
  transform: rotate(180deg);
}
.kb-cat__name {
  font-weight: 600;
  font-size: 15px;
  color: #4c4d4f;
}
.kb-cat__badge {
  background: rgba(2, 172, 180, 0.12);
  color: #02acb4;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}
.kb-cat__body {
  padding: 8px 16px 16px;
}

/* Subcategoria */
.kb-sub {
  margin-top: 14px;
}
.kb-sub__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 4px;
  margin-bottom: 6px;
}
.kb-sub__name {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #02acb4;
}

/* Perguntas */
.kb-questions {
  list-style: none;
  margin: 0;
  padding: 0;
}
.kb-question {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  min-height: 72px;
}
.kb-question__text {
  flex: 1;
  min-width: 0;
}
.kb-question__text h5 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #4c4d4f;
  /* limita a 2 linhas pra alinhar independente do tamanho da pergunta */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.kb-question__text p {
  margin: 0;
  font-size: 13px;
  /* limita a 2 linhas com reticências pra manter altura consistente */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.kb-question__controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.kb-switch {
  margin: 0;
  padding: 0;
}
.kb-state-select {
  width: 180px;
  flex-shrink: 0;
}
/* compacta o select nativo da plataforma e remove o espaco reservado de validacao */
.kb-state-select /deep/ .input-comp {
  margin: 0;
}
.kb-state-select /deep/ .msg-validation,
.kb-state-select /deep/ .labelHelp {
  display: none;
}
.kb-question--disabled {
  opacity: 0.55;
}
.kb-question--disabled .kb-question__text h5 {
  text-decoration: line-through;
}
.kb-empty-hint {
  font-size: 13px;
  font-style: italic;
  padding: 6px 0;
}
</style>
